import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { isCutleryProduct, calculateTotalUnits, getUnitsPerPack } from '../config/productConfig';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { 
    cartItems, 
    isCartOpen, 
    closeCart, 
    removeFromCart, 
    updateQuantity,
    getCartTotal,
    getCartOriginalTotal,
    getCartSavings,
    getItemPrice,
    getItemPriceInfo,
    getItemDetails,
    canIncreaseCartons,
    canDecreaseCartons,
    hasActiveDiscount,
    clearCart 
  } = useCart();

  const handleContinueShopping = () => {
    closeCart();
    navigate('/'); // Navigate to homepage
  };

  if (!isCartOpen) return null;

  const discountActive = hasActiveDiscount();
  const cartSavings = getCartSavings();

  return (
    <>
      <div className="cart-overlay" onClick={closeCart} />

      <div className="cart-drawer">
        <div className="cart-header">
          <h2>
            <ShoppingBag size={24} />
            {language === 'nl' ? 'Winkelwagen' : 'Shopping Cart'}
          </h2>
          <button className="cart-close-btn" onClick={closeCart}>
            <X size={24} />
          </button>
        </div>

        <div className="cart-body">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <ShoppingBag size={64} strokeWidth={1} />
              <p>{language === 'nl' ? 'Uw winkelwagen is leeg' : 'Your cart is empty'}</p>
              <button onClick={handleContinueShopping} className="continue-shopping-btn">
                {language === 'nl' ? 'Verder Winkelen' : 'Continue Shopping'}
              </button>
            </div>
          ) : (
            <div className="cart-items">
              {cartItems.map((item) => {
                const itemDetails = getItemDetails(item);
                const itemPrice = getItemPrice(item);
                const priceInfo = getItemPriceInfo(item);
                const isTieredProduct =
                  item.id === 'edible-cup-110' || item.id === 'edible-cup-220';

                return (
                  <div key={item.cartItemId} className="cart-item">
                    <div className="cart-item-image">
                      <img src={item.image} alt={item.name} />
                    </div>

                    <div className="cart-item-details">
                      <h4>{item.name}</h4>
                      <p className="cart-item-variant">{itemDetails.variant}</p>
                      <div className={`cart-item-price ${priceInfo.hasDiscount ? 'has-discount' : ''}`}>
                        {priceInfo.hasDiscount && (
                          <span className="original-price">€{priceInfo.originalPrice.toFixed(2)}</span>
                        )}
                        <span className={priceInfo.hasDiscount ? 'discounted-price' : ''}>
                          €{itemPrice.toFixed(2)}
                        </span>
                      </div>

                      <div className="cart-item-controls">
                        {/* Tiered products (cups) - show carton controls */}
                        {isTieredProduct ? (
                          <div className="quantity-controls">
                            <button
                              onClick={() =>
                                updateQuantity(item.cartItemId, item.quantity - 1)
                              }
                              disabled={!canDecreaseCartons(item.cartItemId)}
                            >
                              <Minus size={14} />
                            </button>

                            <span>{item.cartons}</span>

                            <button
                              onClick={() =>
                                updateQuantity(item.cartItemId, item.quantity + 1)
                              }
                              disabled={!canIncreaseCartons(item.cartItemId)}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        ) : isCutleryProduct(item.id) ? (
                          // Cutlery products - show pack quantity controls with total units
                          <div className="cutlery-quantity-wrapper">
                            <div className="quantity-controls">
                              <button
                                onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                                disabled={item.quantity <= 1}
                              >
                                <Minus size={14} />
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <div className="total-units-display">
                              <span className="total-units">= {calculateTotalUnits(item.id, item.quantity)} units</span>
                            </div>
                          </div>
                        ) : (
                          // Other products - standard quantity controls
                          <div className="quantity-controls">
                            <button
                              onClick={() => updateQuantity(item.cartItemId, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={14} />
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        )}

                        <button
                          className="remove-item-btn"
                          onClick={() => removeFromCart(item.cartItemId)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-footer">
            <div className={`cart-subtotal ${discountActive ? 'has-discount' : ''}`}>
              <span>{language === 'nl' ? 'Subtotaal' : 'Subtotal'}</span>
              <div className="subtotal-prices">
                {discountActive && (
                  <span className="original-total">€{getCartOriginalTotal().toFixed(2)}</span>
                )}
                <span className={`subtotal-amount ${discountActive ? 'discounted-total' : ''}`}>
                  €{getCartTotal().toFixed(2)}
                </span>
              </div>
            </div>
            {discountActive && cartSavings > 0 && (
              <p className="cart-savings-note">
                {language === 'nl' 
                  ? `Je bespaart €${cartSavings.toFixed(2)}!` 
                  : `You save €${cartSavings.toFixed(2)}!`}
              </p>
            )}
            <p className="cart-shipping-note">
              {language === 'nl'
                ? 'Verzending & belastingen berekend bij afrekenen.'
                : 'Shipping & taxes calculated at checkout.'}
            </p>

            {/* Continue Shopping */}
            <button
              className="continue-shopping-btn"
              onClick={handleContinueShopping}
            >
              {language === 'nl' ? 'Meer toevoegen' : 'Add more items'}
            </button>

            {/* Checkout */}
            <Link to="/shop" className="checkout-btn" onClick={closeCart}>
              {language === 'nl'
                ? 'Bestel je Eetbare Beker'
                : 'Order your Edible cup'}
            </Link>

            <button className="clear-cart-btn" onClick={clearCart}>
              {language === 'nl' ? 'Winkelwagen Legen' : 'Clear Cart'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
